import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState, useLayoutEffect } from "react";
import { useLazyQuery } from "@apollo/client";

import "./DashboardLayout.scss";
import HeaderNav from "../../component/header-nav/HeaderNav";
import CollapsibleSidenav from "../../component/collapsible-sidenav/CollapsibleSidenav";
import {
  GET_CUSTOMERS,
  GET_LINKEDIN_LAST_POST,
} from "../../service/graphql/query";
import { PagePermissionMask } from "../../service/utils/interfaces";
import * as Storage from "../../service/storage";

export type ContextType = { darkMode: boolean };

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("<<Profile Name>>");
  const [permissionMask, setPermissionMask] = useState<PagePermissionMask>();
  const [isTippingUser, setIsTippingUser] = useState(false);
  const [sidenavCollapsed, setSidenavCollapsed] = useState(false);
  const [sidenavExpanding, setSidenavExpanding] = useState(false);
  const [isDarkMode, setDarkMode] = useState(true);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [featuredPostShareId, setFeaturedPostShareId] = useState("");

  const [getCustomers, { data: userData }] = useLazyQuery(GET_CUSTOMERS, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });
  const [getLinkedInLastPost, { data: shareUrnData }] = useLazyQuery(
    GET_LINKEDIN_LAST_POST
  );

  useLayoutEffect(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    setWidth(width);
    setHeight(height);
  }, []);

  useEffect(() => {
    if (getCustomers && getLinkedInLastPost) {
      const handleWindowResize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        setWidth(width);
        setHeight(height);
      };

      Storage.USER_DATA.get()
        .then(async (customerList) => {
          if (customerList && customerList.length > 0) {
            const user: any = customerList[0];
            const name =
              user.displayUsername && user.displayUsername.length > 0
                ? user.displayUsername
                : user.contactName;
            setIsTippingUser(user.category === "Tipping");
            setDisplayName(name);
            setPermissionMask({
              financialPage: user.pageAuth.financialPage,
              weightingPage: user.pageAuth.weightingPage,
              sustainabilityAudit: user.pageAuth.sustainabilityAudit,
              reportingPage: user.pageAuth.reportingPage,
            });
          } else getCustomers({ context: { apiName: "middleware" } });
        })
        .catch(() => {
          getCustomers({ context: { apiName: "middleware" } });
        });
      getLinkedInLastPost({ context: { apiName: "middleware" } });

      window.addEventListener("resize", handleWindowResize);
      return () => window.removeEventListener("resize", handleWindowResize);
    }
  }, [getCustomers, getLinkedInLastPost]);

  useEffect(() => {
    if (sidenavExpanding) {
      setTimeout(() => {
        setSidenavCollapsed(false);
        setSidenavExpanding(false);
      }, 290);
    }
  }, [sidenavExpanding]);

  useEffect(() => {
    if (userData && userData.getCustomers && userData.getCustomers.length > 0) {
      manageUserData(userData);
    }
  }, [userData]);

  const manageUserData = async (userData: any) => {
    const user = userData.getCustomers[0];
    const name =
      user.displayUsername && user.displayUsername.length > 0
        ? user.displayUsername
        : user.contactName;
    setIsTippingUser(user.category === "Tipping");
    setDisplayName(name);
    setPermissionMask({
      financialPage: user.pageAuth.financialPage,
      weightingPage: user.pageAuth.weightingPage,
      sustainabilityAudit: user.pageAuth.sustainabilityAudit,
      reportingPage: user.pageAuth.reportingPage,
    });
    Storage.USER_DATA.set(userData.getCustomers);
  };

  useEffect(() => {
    if (
      shareUrnData &&
      shareUrnData.getLinkedinLastPost &&
      shareUrnData.getLinkedinLastPost.startsWith("urn")
    )
      setFeaturedPostShareId(shareUrnData.getLinkedinLastPost);
  }, [shareUrnData]);

  return (
    <div className="page-wrapper">
      <HeaderNav
        name={displayName}
        darkMode={isDarkMode}
        onThemeSwitch={() => setDarkMode(!isDarkMode)}
        screenWidth={width}
        screenHeight={height}
        permMask={permissionMask}
        isTippingUser={isTippingUser}
      />
      <div className="body">
        <CollapsibleSidenav
          collapsed={sidenavCollapsed}
          expanding={sidenavExpanding}
          darkMode={isDarkMode}
          onCollapseTrigger={() => {
            if (sidenavCollapsed) setSidenavExpanding(true);
            else setSidenavCollapsed(true);
          }}
          featuredPostShareID={featuredPostShareId}
          screenWidth={width}
          screenHeight={height}
          permMask={permissionMask}
          isTippingUser={isTippingUser}
        />
        <Outlet context={{ darkMode: isDarkMode } satisfies ContextType} />
      </div>
    </div>
  );
};

export const useDarkModeCheck = () => {
  return useOutletContext<ContextType>();
};
